import { ensureToken } from './authApi';
import { AUTH_URL } from './apiConfig';

// ── Relevant-skill matching ─────────────────────────────────────────────────
// Which of a candidate's skills does this job actually need?
//
// Sends IDS ONLY. The gateway reads both records from the caller's own
// tenant-scoped database and calls the configured AI service, so this file —
// like every other service module here — never holds a service URL or a
// credential.
//
// NEVER THROWS and never rejects: the only caller is a submission flow, and a
// failed match must degrade to "the recruiter fills Relevant Skills by hand",
// exactly as today. An empty list is the honest answer for every failure.

/**
 * @param {Object} args
 * @param {string} args.jobId
 * @param {string} args.applicantId
 * @param {number} [args.timeoutMs=15000] give up and return [] after this
 * @returns {Promise<string[]>} the candidate's skills this job asks for
 */
export async function matchRelevantSkills({ jobId, applicantId, timeoutMs = 15000 }) {
  if (!jobId || !applicantId) return [];

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const token = await ensureToken();
    const res = await fetch(`${AUTH_URL}/skill-match`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ jobId, applicantId }),
      signal: controller.signal,
    });
    if (!res.ok) return [];
    const json = await res.json();
    const skills = json?.data?.relevantSkills ?? json?.relevantSkills;
    return Array.isArray(skills) ? skills.filter((s) => typeof s === 'string' && s.trim()) : [];
  } catch {
    // Aborted, offline, 5xx, malformed body — all the same outcome.
    return [];
  } finally {
    clearTimeout(timer);
  }
}
